import React, { useEffect, useState } from 'react';
import AdminLayout from '../components/Layout/AdminLayout';
import { FaSearch, FaPhoneAlt, FaClock, FaSpinner } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify'; 
import { Lead, User } from '../api/types'; 
import { getSubordinatesByParent, assignLeads } from '../api/api'; 

const HotLeadsPage: React.FC = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // Page state ab sessionStorage se aayega
  const [page, setPage] = useState<number>(() => {
    const savedPage = sessionStorage.getItem('hotLeadsPage');
    return savedPage ? parseInt(savedPage, 10) : 0;
  });
  
  const [totalPages, setTotalPages] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedSource, setSelectedSource] = useState<string>('');
  
  // Remark filter state
  const [selectedRemark, setSelectedRemark] = useState<string>('');
  
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [filteredLeads, setFilteredLeads] = useState<Lead[]>([]);

  // Assignment States
  const [selectedLeadIds, setSelectedLeadIds] = useState<string[]>([]);
  const [subordinates, setSubordinates] = useState<User[]>([]);
  const [assignToUserId, setAssignToUserId] = useState<string>('');

  // --- NAYA: Live Call Status State ---
  const [liveCalls, setLiveCalls] = useState<Record<string, { status: string, duration?: number, lastCall?: string }>>({});
  const navigate = useNavigate();

  // --- ROLE CHECK LOGIC ---
  const checkIsAgent = () => {
    const token = localStorage.getItem('token');
    if (!token) return false;
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const roleStr = JSON.stringify(payload).toUpperCase();
        return roleStr.includes('AGENT');
    } catch(e) {
        return false;
    }
  };
  const isAgent = checkIsAgent();

  // --- NUMBER MASKING LOGIC ---
  const maskMobile = (mobile: string | undefined) => {
    if (!mobile || mobile.length < 10) return mobile || 'N/A';
    return mobile.substring(0, 2) + "******" + mobile.substring(8);
  };

  // --- CALL LOGIC (LIVE TRACKING) ---
  const initiateCall = async (lead: Lead) => {
    try {
        let agentMobile = "919217639679"; // Default Number for Admin/Other Agents

        // Token ko decode karke dekhte hain ki kaunsa agent login hai
        try {
            const token = localStorage.getItem('token');
            if (token) {
                const payload = JSON.parse(atob(token.split('.')[1]));
                const username = payload.sub || payload.username; // Spring Boot me username mostly 'sub' me hota hai
                
                // 🔥 AGENT SPECIFIC LOGIC
                if (username === 'deep123') {
                    agentMobile = "917428650572"; // deep123 ka personal number
                }
                if (username === 'rj123') {
                    agentMobile = "919891501149"; // rj123 ka personal number
                }
                if (username === 'psr123') {
                    agentMobile = "919217639679"; // psr123 ka personal number
                }
                // if (username === 'pc123') {
                //     agentMobile = "919217917894"; // pc123 ka personal number
                // }
            }
        } catch (e) {
            console.error("User identity check failed", e);
        }

        // Customer Number Clean-up (Ensure 91 is added)
        let custMobile = lead.mobile || "";
        if (custMobile.length === 10) {
            custMobile = "91" + custMobile;
        }

        const agentId = localStorage.getItem('userId') || 1; 

        // UI ko "DIALING..." set karein
        setLiveCalls(prev => ({ ...prev, [lead.id]: { status: 'DIALING...' } }));

        // Backend Hit Karein
        // const response = await fetch('http://localhost:8483/api/call/make-call', {
        const response = await fetch('https://www.ayushvatika.com/api/call/make-call', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                customer_number: custMobile, 
                agent_extension: agentMobile, // Ab deep123 ke liye uska alag number jayega!
                customer_id: lead.id,
                agent_id: agentId
            })
        });

        const data = await response.json();
        
        if (data.success && data.tata_ref_id) {
            toast.success(`Calling ${lead.firstName}... Please pick up your phone!`);
            
            // Polling Logic (Live Status Update)
            const intervalId = setInterval(async () => {
                try {
                    // const statusRes = await fetch(`http://localhost:8483/api/call/status/${data.tata_ref_id}`);
                    const statusRes = await fetch(`https://www.ayushvatika.com/api/call/status/${data.tata_ref_id}`);
                    const statusData = await statusRes.json();
                    
                    if (statusData.success) {
                        const currentStatus = statusData.data.status;
                        
                        if (currentStatus !== 'Initiated' && currentStatus !== 'Unknown') {
                            clearInterval(intervalId); // Polling Stop
                            setLiveCalls(prev => ({ 
                                ...prev, 
                                [lead.id]: { status: currentStatus, duration: statusData.data.duration } 
                            }));
                        } else {
                            setLiveCalls(prev => ({ ...prev, [lead.id]: { status: 'LIVE 📞' } }));
                        }
                    }
                } catch (err) {
                    clearInterval(intervalId);
                }
            }, 3000);

        } else {
            setLiveCalls(prev => ({ ...prev, [lead.id]: { status: 'FAILED' } }));
            toast.error("Call failed: " + data.message);
        }
    } catch (error) {
        setLiveCalls(prev => ({ ...prev, [lead.id]: { status: 'ERROR' } }));
        toast.error("Calling Server is down!");
    }
  };


  
  // --- Theme Helpers ---
  const inputClass = `w-full p-2.5 rounded border outline-none transition-colors duration-300 
  bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 placeholder-gray-400
  dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:focus:border-blue-500 dark:placeholder-gray-500
  disabled:opacity-60 disabled:cursor-not-allowed`;

  const roleColorMap: Record<string, string> = {
  SUPER_ADMIN:
    "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300",
  ADMIN:
    "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300",
  MANAGER:
    "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  TEAM_LEAD:
    "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
  AGENT:
   "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
};

  // 1. Fetch Leads
  const fetchLeads = async (pageNumber: number) => {
    setLoading(true);
    setError(null);
    try {
      const authToken = localStorage.getItem('token');
      
      let url = `https://www.ayushvatika.com/api/admin/leads?page=${pageNumber}&size=100`;
      if (searchQuery.trim() !== '') {
          url += `&search=${encodeURIComponent(searchQuery)}`;
      }

      if (selectedSource) {
          url += `&source=${encodeURIComponent(selectedSource)}`;
      }
      if (selectedRemark && selectedRemark !== 'UNWORKED') {
          url += `&remark=${encodeURIComponent(selectedRemark)}`;
      }
      if (startDate) {
          url += `&startDate=${startDate}T00:00:00`;
      }
      if (endDate) {
          url += `&endDate=${endDate}T23:59:59`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch leads.');

      const data = await response.json();
      const pageLeads = data.content;

      // Har lead ka remark detail API se fetch karke attach karna
      const leadsWithRemarks = await Promise.all(
        pageLeads.map(async (lead: any) => {
          try {
            const detailRes = await fetch(
              `https://www.ayushvatika.com/api/admin/leads/details/${lead.id}`,
              { headers: { 'Authorization': `Bearer ${authToken}` } }
            );
            if (detailRes.ok) {
              const detailData = await detailRes.json();
              return { ...lead, remark: detailData.remark }; 
            }
          } catch (err) {
            console.error("Failed to fetch detail for lead", lead.id);
          }
          return lead; 
        })
      );

      // Purana data UI me set ho gaya
      setLeads(leadsWithRemarks); 

      // 🔥 NAYA CODE: Bulk Status Fetching taaki refresh par status na hate
      const ids = leadsWithRemarks.map((l: any) => l.id);
      if (ids.length > 0) {
          try {
            //   const statusRes = await fetch('http://localhost:8483/api/call/bulk-status', {
              const statusRes = await fetch('https://www.ayushvatika.com/api/call/bulk-status', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ customer_ids: ids })
              });
              const statusData = await statusRes.json();
              if (statusData.success && statusData.data) {
                  setLiveCalls(statusData.data); // Database wala exact status UI me fill ho jayega
              }
          } catch (e) {
              console.error("Failed to load historical statuses", e);
          }
      }
      // 🔥 NAYA CODE END

      setTotalPages(data.totalPages);
      setPage(data.number);
      setSelectedLeadIds([]);
    } catch (err) {
      setError('Could not fetch leads. Please try again later.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // 2. Fetch Subordinates
  useEffect(() => {
    const fetchSubs = async () => {
        try {
            const response = await getSubordinatesByParent(null);
            setSubordinates(response.data);
        } catch (err) {
            console.error("Failed to fetch subordinates", err);
        }
    };
    fetchSubs();
  }, []);

  // Set sessionStorage and Fetch WHENEVER a filter or page changes
  useEffect(() => {
    sessionStorage.setItem('hotLeadsPage', page.toString());
    
    const delayDebounceFn = setTimeout(() => {
      fetchLeads(page);
    }, 400);

    return () => clearTimeout(delayDebounceFn);
  }, [page, selectedSource, selectedRemark, startDate, endDate, searchQuery]);

  // 3. Filter & Sort Logic (Frontend)
  useEffect(() => {
    let result = [...leads];

    if (selectedRemark === 'UNWORKED') {
      result = result.filter(lead => !(lead as any).remark || (lead as any).remark.trim() === '');
    }

    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      result = result.filter(lead =>
        (lead.firstName?.toLowerCase() || '').includes(query) ||
        (lead.lastName?.toLowerCase() || '').includes(query) ||
        (lead.email?.toLowerCase() || '').includes(query) ||
        (lead.mobile || '').includes(query)
      );
    }

    // Priority Sort
    const getPriority = (remark?: string) => {
        if (!remark || remark.trim() === '') return 1; 
        if (remark === 'Not Interested') return 3; 
        return 2; 
    };

    result.sort((a, b) => {
        const priorityA = getPriority((a as any).remark);
        const priorityB = getPriority((b as any).remark);
        return priorityA - priorityB;
    });

    setFilteredLeads(result);
  }, [leads, searchQuery, selectedRemark]);

  const handleSelectLead = (id: string) => {
    if (selectedLeadIds.includes(id)) {
      setSelectedLeadIds(selectedLeadIds.filter(leadId => leadId !== id));
    } else {
      setSelectedLeadIds([...selectedLeadIds, id]);
    }
  };

  const handleSelectAll = () => {
      if (selectedLeadIds.length === filteredLeads.length) {
          setSelectedLeadIds([]);
      } else {
          setSelectedLeadIds(filteredLeads.map(l => l.id));
      }
  };

  const handleAssignSubmit = async () => {
      if (!assignToUserId || selectedLeadIds.length === 0) {
          toast.error("Please select leads and a staff member.");
          return;
      }

      try {
          await assignLeads({
              leadIds: selectedLeadIds,
              assignToUserId: parseInt(assignToUserId)
          });
          toast.success(`Successfully assigned ${selectedLeadIds.length} leads!`);
          setSelectedLeadIds([]);
          setAssignToUserId('');
          fetchLeads(page);
      } catch (error) {
          toast.error("Failed to assign leads. Please try again.");
      }
  };

  const renderPagination = () => {
      if (totalPages <= 1) return null;

      const maxVisible = 5; // Ek baar me 5 page numbers dikhenge
      let start = Math.max(0, page - 2);
      let end = Math.min(totalPages - 1, start + maxVisible - 1);

      if (end - start + 1 < maxVisible) {
          start = Math.max(0, end - maxVisible + 1);
      }

      const pages = [];
      for (let i = start; i <= end; i++) {
          pages.push(i);
      }

      const btnClass = "px-3 py-1.5 md:px-4 md:py-2 text-sm rounded shadow-sm border transition-all disabled:opacity-50 disabled:cursor-not-allowed";
      const inactiveClass = "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700";
      const activeClass = "bg-blue-600 text-white border-blue-600 shadow-md font-bold";

      return (
          <div className="flex justify-center items-center gap-1.5 md:gap-2 mt-8 flex-wrap">
              <button onClick={() => setPage(0)} disabled={page === 0} className={`${btnClass} ${inactiveClass}`}>
                  First
              </button>
              <button onClick={() => setPage(page - 1)} disabled={page === 0} className={`${btnClass} ${inactiveClass}`}>
                  Prev
              </button>

              {pages.map(p => (
                  <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`${btnClass} ${page === p ? activeClass : inactiveClass}`}
                  >
                      {p + 1}
                  </button>
              ))}

              {end < totalPages - 1 && (
                  <span className="text-gray-500 px-2">...</span>
              )}

              <button onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1} className={`${btnClass} ${inactiveClass}`}>
                  Next
              </button>
              <button onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1} className={`${btnClass} ${inactiveClass}`}>
                  Last ({totalPages})
              </button>
          </div>
      );
  };

  return (
    <AdminLayout>
      <div className="container mx-auto p-6 min-h-screen transition-colors duration-300 bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-white">
        
        <h2 className="text-3xl font-bold mb-6 text-green-600 dark:text-green-500">Hot Leads</h2>

        {/* ASSIGNMENT BAR */}
        {subordinates.length > 0 && !isAgent && (
            <div className="p-4 mb-6 rounded-lg shadow-md border flex flex-wrap gap-4 items-center transition-colors duration-300 bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-700">
                <span className="font-semibold text-gray-700 dark:text-white">
                    Assign Selected ({selectedLeadIds.length}):
                </span>
                <select 
                    className={`${inputClass} w-auto min-w-[200px]`}
                    value={assignToUserId}
                    onChange={(e) => setAssignToUserId(e.target.value)}
                >
                    <option value="">-- Select Staff --</option>
                    {subordinates.map(sub => (
                        <option key={sub.id} value={sub.id}>
                            {sub.username} ({sub.role})
                        </option>
                    ))}
                </select>
                <button 
                    onClick={handleAssignSubmit}
                    disabled={selectedLeadIds.length === 0 || !assignToUserId}
                    className={`px-4 py-2 rounded font-semibold transition shadow-md ${
                        selectedLeadIds.length > 0 && assignToUserId 
                        ? 'bg-green-600 text-white hover:bg-green-700' 
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-400'
                    }`}
                >
                    Assign Leads
                </button>
            </div>
        )}

        {/* Search and Filter Controls */}
        <div className="p-4 rounded-lg shadow-md border mb-6 grid grid-cols-1 md:grid-cols-5 gap-4 transition-colors duration-300 bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-700">
          <div className="relative md:col-span-1">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400">
              <FaSearch />
            </div>
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
              className={`${inputClass} pl-10`}
            />
          </div>

          <select
            value={selectedSource}
            onChange={(e) => { setSelectedSource(e.target.value); setPage(0); }}
            className={inputClass}
          >
            <option value="">All Sources</option>
            <option value="MANUAL">Manual</option>
            <option value="BULK">Bulk</option>
            <option value="WEBSITE_REGISTERED">Website Registered</option>
          </select>
          
          <select
            value={selectedRemark}
            onChange={(e) => { setSelectedRemark(e.target.value); setPage(0); }}
            className={inputClass}
          >
            <option value="">All Remarks</option>
            <option value="UNWORKED">Unworked (No Remark)</option>
            <option value="Call Back Scheduled">Call Back Scheduled</option>
            <option value="Call Not Pick">Call Not Pick</option>
            <option value="Switch off / Not Reachable">Switch off / Not Reachable</option>
            <option value="other">Other</option>
            <option value="Not Interested">Not Interested</option>
          </select>
          
          <input
            type="date"
            value={startDate}
            onChange={(e) => { setStartDate(e.target.value); setPage(0); }}
            className={inputClass}
          />
          
          <input
            type="date"
            value={endDate}
            onChange={(e) => { setEndDate(e.target.value); setPage(0); }}
            className={inputClass}
          />
        </div>

        {loading && <div className="text-center p-8 text-gray-600 dark:text-gray-300">Loading...</div>}
        {error && <div className="p-4 rounded-lg bg-red-100 border border-red-400 text-red-700 text-center dark:bg-red-900/20 dark:border-red-500 dark:text-red-200">{error}</div>}
        
        {!loading && !error && (
          <div className="animate-slideUp">
             <div className="rounded-lg shadow-md border overflow-hidden transition-colors duration-300 bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-700">
              <div className="overflow-x-auto custom-scrollbar">
                <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
                    <thead className="uppercase text-xs bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                    <tr>
                        <th className="px-3 py-3 w-10 text-center">
                            <input 
                                type="checkbox" 
                                onChange={handleSelectAll}
                                checked={filteredLeads.length > 0 && selectedLeadIds.length === filteredLeads.length}
                                className="cursor-pointer w-4 h-4 rounded text-green-600 focus:ring-green-500 bg-gray-200 border-gray-300 dark:bg-gray-700 dark:border-gray-600"
                            />
                        </th>
                        <th className="px-3 py-3">Name</th>
                        <th className="px-3 py-3">Email</th>
                        <th className="px-3 py-3">Mobile</th>
                        {/* Live Status Column */}
                        <th className="px-3 py-3">Live Status</th>
                        <th className="px-3 py-3">Remark</th>
                        <th className="px-3 py-3">{isAgent ? 'Action' : 'Assigned To'}</th>
                    </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredLeads.map((lead) => (
                        <tr key={lead.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                        <td className="px-3 py-3 text-center">
                            <input 
                                type="checkbox" 
                                checked={selectedLeadIds.includes(lead.id)}
                                onChange={() => handleSelectLead(lead.id)}
                                className="cursor-pointer w-4 h-4 rounded text-green-600 focus:ring-green-500 bg-gray-200 border-gray-300 dark:bg-gray-700 dark:border-gray-600"
                            />
                        </td>
                        <td 
                            className="px-3 py-3 font-medium cursor-pointer text-gray-900 dark:text-white hover:text-green-600 dark:hover:text-green-400 transition-colors"
                            onClick={() => navigate(`/admin/leads/sales/${lead.id}`)}
                        >
                            {lead.firstName} {lead.lastName}
                        </td>
                        <td className="px-3 py-3">{lead.email}</td>
                        
                        {/* --- CONDITIONAL MOBILE COLUMN --- */}
                        <td className="px-3 py-3">
                            {isAgent ? (
                                <span className="font-mono text-gray-700 dark:text-gray-300">
                                    {maskMobile(lead.mobile)}
                                </span>
                            ) : (
                                <span 
                                    onClick={(e) => { e.stopPropagation(); initiateCall(lead); }}
                                    className="font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline cursor-pointer flex items-center gap-2"
                                    title="Click to Call"
                                >
                                    {lead.mobile} <FaPhoneAlt size={10} className="opacity-70" />
                                </span>
                            )}
                        </td>

                        {/* --- LIVE STATUS CELL --- */}
                  <td className="px-3 py-3">
    {liveCalls[lead.id] ? (
        <div className="flex flex-col gap-1">
            <span className={`text-[11px] font-bold px-2 py-1.5 rounded flex items-center gap-1.5 w-max shadow-sm border
                ${liveCalls[lead.id].status === 'DIALING...' || liveCalls[lead.id].status === 'LIVE 📞' ? 'bg-yellow-50 text-yellow-700 border-yellow-300 dark:bg-yellow-900/40 dark:text-yellow-400 dark:border-yellow-700' : ''}
                ${liveCalls[lead.id].status === 'Answered' ? 'bg-green-50 text-green-700 border-green-300 dark:bg-green-900/40 dark:text-green-400 dark:border-green-700' : ''}
                ${liveCalls[lead.id].status.includes('Missed') || liveCalls[lead.id].status === 'FAILED' ? 'bg-red-50 text-red-700 border-red-300 dark:bg-red-900/40 dark:text-red-400 dark:border-red-700' : ''}
            `}>
                {(liveCalls[lead.id].status === 'DIALING...' || liveCalls[lead.id].status === 'LIVE 📞') && <FaSpinner className="animate-spin text-yellow-600 dark:text-yellow-400" />}
                {liveCalls[lead.id].status}
                {liveCalls[lead.id].duration !== undefined && liveCalls[lead.id].duration! > 0 && (
                    <span className="ml-1 opacity-80">({liveCalls[lead.id].duration}s)</span>
                )}
            </span>
            
            {/* Last Call Timing Logic */}
      {liveCalls[lead.id].status !== 'DIALING...' && liveCalls[lead.id].status !== 'LIVE 📞' && (
    <span className={`text-[10px] font-semibold flex items-center gap-1 mt-0.5
        ${liveCalls[lead.id].status === 'Answered' ? 'text-green-600' : 'text-red-500'}
    `}>
        <FaClock size={10} /> 
        Last Call: {liveCalls[lead.id].lastCall 
            ? new Date(liveCalls[lead.id].lastCall!).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }) 
            : new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}
    </span>
)}
        </div>
    ) : (
        <span className="text-gray-400 text-[11px] italic flex items-center gap-1"><FaClock size={10} /> Not called yet</span>
    )}
</td>
                        
                        {/* Remark Data Cell */}
                        <td className="px-3 py-3">
                            <div 
                                className="max-w-[130px] truncate text-xs font-medium" 
                                title={(lead as any).remark || 'Unworked'} 
                            >
                                {(lead as any).remark ? (
                                    <span className="text-gray-800 dark:text-gray-200">
                                        {(lead as any).remark}
                                    </span>
                                ) : (
                                    <span className="text-gray-400 dark:text-gray-500 italic">
                                        Unworked
                                    </span>
                                )}
                            </div>
                        </td>

                        <td className="px-3 py-3 align-top">
                            {isAgent ? (
                                <button 
                                    onClick={(e) => { e.stopPropagation(); initiateCall(lead); }} 
                                    className="px-4 py-1.5 rounded bg-green-100 text-green-700 hover:bg-green-600 hover:text-white dark:bg-green-900/40 dark:text-green-400 dark:hover:bg-green-600 dark:hover:text-white transition-colors flex items-center gap-2 shadow-sm font-semibold text-xs border border-green-300 dark:border-green-700" 
                                    title="Call this lead"
                                >
                                    <FaPhoneAlt size={10} /> Call
                                </button>
                            ) : (
                                lead.assignedToUserId ? (
                                    (() => {
                                    const staff = subordinates.find(
                                        sub => sub.id === lead.assignedToUserId
                                    );

                                    if (!staff)
                                        return (
                                        <span className="text-xs text-gray-500">
                                            ID: {lead.assignedToUserId}
                                        </span>
                                        );

                                    return (
                                        <div className="flex flex-col gap-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="px-2.5 py-1 rounded text-xs font-semibold bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700">
                                            {staff.username}
                                            </span>

                                        {staff.role && (
                                        <span
                                            className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${roleColorMap[staff.role] || "bg-teal-100 text-teal-600 dark:bg-teal-800 dark:text-teal-300"}`}
                                        >
                                            {staff.role.replace("_", " ")}
                                        </span>
                                        )}
                                        </div>
                                        </div>
                                    );
                                    })()
                                ) : (
                                    <span className="text-gray-400 text-xs italic">Unassigned</span>
                                )
                            )}
                        </td>
                        </tr>
                    ))}
                    {filteredLeads.length === 0 && (
                        <tr>
                        <td colSpan={7} className="text-center py-8 text-gray-500 dark:text-gray-400">
                            No leads found.
                        </td>
                        </tr>
                    )}
                    </tbody>
                </table>
              </div>
            </div>

            {/* Pagination Controls */}
            {renderPagination()}
          </div>
        )}
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #9ca3af; border-radius: 3px; }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #374151; }
        .animate-slideUp { animation: slideUp 0.4s ease-out; }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </AdminLayout>
  );
};

export default HotLeadsPage;